/**
 * Super Admin → Features: per-tenant capability switches from the server's feature registry (generic — any group
 * the registry defines shows up here: the Location Service Engine and Multi-shop checkout today). With more than one
 * group a picker chooses which one the page is showing; ``?group=<key>`` keeps the choice in the address.
 *
 * Some groups can't be on together for one tenant (multi-shop checkout and the Location Service Engine): the server
 * says which via ``blocked_by`` and refuses the save either way — here the master switch just explains why.
 *
 * The master switch is the parent gate: while it is OFF every module is inactive for that tenant (the toggles are
 * shown greyed out and keep their saved position for when the master is switched back on). Modules that need
 * another module say so; "extension point" modules wait for a client decision and can't be switched on.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Lock, Power, Search, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

function Switch({ checked, onChange, disabled, testid, label }) {
    return (
        <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} data-testid={testid}
                onClick={() => !disabled && onChange(!checked)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition ${checked ? "bg-primary" : "bg-muted-foreground/30"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${checked ? "left-[22px]" : "left-0.5"}`} />
        </button>
    );
}

export default function SuperFeatures() {
    const [params, setParams] = useSearchParams();
    const [registry, setRegistry] = useState(null);
    const [tenants, setTenants] = useState(null);
    const [q, setQ] = useState("");
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    // ``?for=<tenant id>`` — never ``?tenant=``: that query key is the app-wide tenant-slug override (lib/api.js).
    const selectedId = params.get("for");
    const load = () => api.get("/super/feature-groups/tenants").then(({ data }) => setTenants(data.tenants || []))
        .catch(() => setTenants([]));
    useEffect(() => {
        api.get("/super/feature-registry").then(({ data }) => setRegistry(data.groups || [])).catch(() => setRegistry([]));
        load();
    }, []);
    const group = registry?.find((g) => g.key === params.get("group")) || registry?.[0];
    const selected = tenants?.find((t) => t.id === selectedId) || null;
    const setParam = (key, value) => setParams((prev) => { const next = new URLSearchParams(prev); next.set(key, value); return next; });
    useEffect(() => {
        if (!selected || !group) { setDraft(null); return; }
        const st = selected.groups[group.key] || { enabled: false, modules: {} };
        setDraft({ enabled: !!st.enabled, modules: { ...(st.modules || {}) } });
    }, [selected?.id, group?.key, tenants]);   // eslint-disable-line react-hooks/exhaustive-deps

    const list = useMemo(() => (tenants || []).filter((t) => !q || `${t.name} ${t.slug}`.toLowerCase().includes(q.toLowerCase())), [tenants, q]);
    const sections = useMemo(() => {
        const out = [];
        (group?.modules || []).forEach((m) => {
            let s = out.find((x) => x.name === m.section);
            if (!s) { s = { name: m.section, modules: [] }; out.push(s); }
            s.modules.push(m);
        });
        return out;
    }, [group]);
    const saved = selected && group ? selected.groups[group.key] : null;
    // Groups that are ON for this tenant and can't be combined with this one. Switching OFF is always allowed.
    const blockedBy = saved?.blocked_by || [];
    const cannotEnable = blockedBy.length > 0 && !saved?.enabled;
    const dirty = !!(draft && saved && (draft.enabled !== !!saved.enabled
        || (group?.modules || []).some((m) => !!draft.modules[m.key] !== !!(saved.modules || {})[m.key])));
    const effective = (() => {
        if (!draft?.enabled || !group) return new Set();
        const on = new Set(group.modules.filter((m) => draft.modules[m.key] && m.status === "available").map((m) => m.key));
        let changed = true;
        while (changed) {
            changed = false;
            group.modules.forEach((m) => { if (on.has(m.key) && m.requires.some((r) => !on.has(r))) { on.delete(m.key); changed = true; } });
        }
        return on;
    })();

    const save = async () => {
        setSaving(true);
        try {
            const modules = Object.fromEntries(group.modules.filter((m) => m.status === "available").map((m) => [m.key, !!draft.modules[m.key]]));
            await api.put(`/super/tenants/${selected.id}/feature-groups/${group.key}`, { enabled: draft.enabled, modules });
            toast.success(`${selected.name}: ${group.label} ${draft.enabled ? `ON · ${effective.size} module(s)` : "OFF"}`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setSaving(false); }
    };
    const setAll = (on) => setDraft((d) => ({ ...d, modules: Object.fromEntries(group.modules.filter((m) => m.status === "available").map((m) => [m.key, on])) }));
    // Dependencies: a module can only be on together with what it needs; switching a requirement off switches its
    // dependents off too (the server refuses an invalid combination either way).
    const setModule = (key, value) => setDraft((d) => {
        const mods = { ...d.modules, [key]: value };
        if (!value) {
            let changed = true;
            while (changed) {
                changed = false;
                group.modules.forEach((m) => { if (mods[m.key] && m.requires.some((r) => !mods[r])) { mods[m.key] = false; changed = true; } });
            }
        }
        return { ...d, modules: mods };
    });

    if (!registry || !tenants) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>;
    if (!group) return <p className="p-6 text-sm text-muted-foreground">No feature groups are defined.</p>;

    return (
        <div data-testid="super-features">
            <div>
                <h1 className="text-2xl font-bold inline-flex items-center gap-2"><SlidersHorizontal size={22} /> Features</h1>
                {registry.length > 1 && (
                    <div className="mt-3 inline-flex flex-wrap gap-1 p-0.5 bg-muted rounded-lg" role="tablist" aria-label="Feature group" data-testid="features-groups">
                        {registry.map((g) => (
                            <button key={g.key} type="button" role="tab" aria-selected={g.key === group.key} onClick={() => setParam("group", g.key)}
                                    data-testid={`features-group-${g.key}`}
                                    className={`px-3 py-1.5 rounded-md text-sm font-semibold ${g.key === group.key ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                                {g.label}
                            </button>
                        ))}
                    </div>
                )}
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{group.label}: {group.description}</p>
            </div>
            <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
                {/* On phones the chosen tenant's switches come first; the list stays one tap below. */}
                <aside className={`bg-card border border-border rounded-2xl overflow-hidden self-start ${selected ? "order-2 lg:order-1" : ""}`}>
                    <label className="flex items-center gap-2 px-3 py-2 border-b border-border">
                        <Search size={15} className="text-muted-foreground" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a tenant" className="bg-transparent outline-none text-sm flex-1 min-w-0" data-testid="features-search" />
                    </label>
                    <ul className={`${selected ? "max-h-[40vh]" : "max-h-[60vh]"} lg:max-h-[60vh] overflow-y-auto divide-y divide-border`}>
                        {list.map((t) => {
                            const g = t.groups[group.key];
                            return (
                                <li key={t.id}>
                                    <button type="button" onClick={() => setParam("for", t.id)} data-testid={`features-tenant-${t.slug}`}
                                            className={`w-full text-left px-3 py-2.5 flex items-center gap-2 ${t.id === selectedId ? "bg-primary/10" : "hover:bg-muted/50"}`}>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm font-semibold truncate">{t.name}</span>
                                            <span className="block text-[11px] text-muted-foreground truncate">{t.slug}</span>
                                        </span>
                                        {g?.enabled
                                            ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">ON · {g.effective.length}</span>
                                            : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">OFF</span>}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </aside>

                {!selected || !draft ? (
                    <div className="bg-card border border-dashed border-border rounded-2xl p-8 text-center text-sm text-muted-foreground order-1 lg:order-2" data-testid="features-pick">
                        Pick a tenant to see and change its capabilities. Every tenant starts with everything off.
                    </div>
                ) : (
                    <section className="bg-card border border-border rounded-2xl order-1 lg:order-2 min-w-0" data-testid="features-panel">
                        <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
                            <div className="w-full sm:w-auto sm:flex-1 min-w-0">
                                <p className="font-bold truncate">{selected.name}</p>
                                <p className="text-xs text-muted-foreground">{draft.enabled ? `${effective.size} module(s) will be active` : "Nothing is active for this tenant"}</p>
                            </div>
                            <label className="inline-flex items-center gap-2 text-sm font-semibold">
                                <Power size={16} className={draft.enabled ? "text-emerald-600" : "text-muted-foreground"} /> {group.label}
                                <Switch checked={draft.enabled} disabled={cannotEnable} onChange={(v) => setDraft({ ...draft, enabled: v })} testid="features-master" label={`${group.label} master switch`} />
                            </label>
                        </div>
                        {blockedBy.length > 0 && (
                            <p className="mx-4 mt-4 text-xs rounded-xl bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900 px-3 py-2 flex items-start gap-2" data-testid="features-blocked">
                                <Lock size={14} className="shrink-0 mt-0.5" />
                                <span>{group.label} can&apos;t be used together with {blockedBy.join(", ")}, which is on for this tenant. Switch {blockedBy.join(", ")} off first.</span>
                            </p>
                        )}
                        {!draft.enabled && (
                            <p className="mx-4 mt-4 text-xs rounded-xl bg-muted px-3 py-2 inline-flex items-center gap-2" data-testid="features-master-off">
                                <AlertTriangle size={14} /> Master switch is OFF — every module below is inactive for this tenant. Saved positions are kept.
                            </p>
                        )}
                        {group.modules.length > 1 && (
                            <div className="px-4 pt-3 flex gap-2">
                                <button type="button" disabled={!draft.enabled} onClick={() => setAll(true)} className="text-xs font-semibold rounded-lg border border-border px-3 py-1.5 disabled:opacity-40">Select all available</button>
                                <button type="button" disabled={!draft.enabled} onClick={() => setAll(false)} className="text-xs font-semibold rounded-lg border border-border px-3 py-1.5 disabled:opacity-40">Clear</button>
                            </div>
                        )}
                        <div className="p-4 grid gap-4 md:grid-cols-2">
                            {sections.map((s) => (
                                <div key={s.name} className="rounded-xl border border-border">
                                    <p className="px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-muted-foreground border-b border-border">{s.name}</p>
                                    <ul className="divide-y divide-border">
                                        {s.modules.map((m) => {
                                            const locked = m.status !== "available";
                                            const on = !!draft.modules[m.key];
                                            const missing = m.requires.filter((r) => !draft.modules[r]);
                                            const active = effective.has(m.key);
                                            return (
                                                <li key={m.key} className={`px-3 py-2.5 flex items-start gap-3 ${!draft.enabled ? "opacity-55" : ""}`} data-testid={`features-module-${m.key}`}>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold inline-flex items-center gap-1.5">
                                                            {locked && <Lock size={12} />} {m.label}
                                                            {active && <CheckCircle2 size={13} className="text-emerald-600" aria-label="active" />}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>
                                                        {locked && <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">Extension point — {m.pending_decision}</p>}
                                                        {missing.length > 0 && !locked && <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5" data-testid={`features-needs-${m.key}`}>Needs: {missing.map((r) => group.modules.find((x) => x.key === r)?.label || r).join(", ")}</p>}
                                                    </div>
                                                    <Switch checked={on && !locked} disabled={!draft.enabled || locked || (!on && missing.length > 0)} label={m.label} testid={`features-toggle-${m.key}`}
                                                            onChange={(v) => setModule(m.key, v)} />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                        <div className="sticky bottom-0 bg-card border-t border-border p-3 flex items-center justify-end gap-2 rounded-b-2xl">
                            {dirty && <span className="text-xs text-muted-foreground mr-auto">Unsaved changes</span>}
                            <button type="button" disabled={!dirty || saving} onClick={save} data-testid="features-save"
                                    className="bg-primary text-primary-foreground rounded-xl px-5 min-h-[40px] text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                                {saving && <Loader2 size={15} className="animate-spin" />} Save
                            </button>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
